/**
 * How fast the hangar screen updates (Kyle, 2026-10-02: "the weather display needs to update more often.
 * default is as often as possible and it's an adjustable setting").
 *
 * The owner picks a setting per site (`site.screenRefreshS`: 0 = as fast as possible, else 5, 15, 30, 60
 * or 300 seconds). Each check is ONE small request, the latest readings and limit states together, sent
 * with the ETag of the last answer. When nothing changed, SkyWeather answers "304 Not Modified" with no
 * body, so a fast setting costs headers, not data. Only when a reading actually changes does a body come
 * back, and the new reading is appended to the on-screen trend instead of re-downloading two hours of it.
 *
 * The full picture (site details, the whole trend) is reloaded every `FULL_RELOAD_MS`, which also picks up
 * a changed setting, a renamed source, and anything the incremental path could have missed.
 */

import type { Observation } from '@skyweather/weather-core'
import type { KioskData, LimitStatus, SourceReading } from './types.js'

/** "As fast as possible": one check every this many seconds. */
export const FASTEST_SCREEN_CHECK_S = 3

/** The owner's choices, in seconds; 0 = as fast as possible. Must match the API's `SCREEN_REFRESH_CHOICES`. */
export const SCREEN_REFRESH_CHOICES = [0, 5, 15, 30, 60, 300] as const

/** Reload everything this often, whatever the setting. */
export const FULL_RELOAD_MS = 5 * 60_000

/**
 * Re-read the site's own settings (what the screen shows, how often it checks) this often, with the last
 * answer's ETag: a 304 nearly every time, so an owner's change reaches the TV within half a minute instead
 * of at the next full reload (Kyle will flip a switch and look at the screen).
 */
export const SITE_RECHECK_MS = 30_000

/** Milliseconds between checks for a site's setting. Unknown or missing values mean as fast as possible. */
export function screenCheckMs(screenRefreshS: number | undefined | null): number {
  const s = typeof screenRefreshS === 'number' && (SCREEN_REFRESH_CHOICES as readonly number[]).includes(screenRefreshS) ? screenRefreshS : 0
  return (s === 0 ? FASTEST_SCREEN_CHECK_S : s) * 1000
}

/** The setting as an owner reads it. */
export function screenRefreshLabel(screenRefreshS: number): string {
  if (screenRefreshS === 0) return `As fast as possible (every ${FASTEST_SCREEN_CHECK_S} seconds)`
  if (screenRefreshS < 60) return `Every ${screenRefreshS} seconds`
  if (screenRefreshS === 60) return 'Every minute'
  return `Every ${screenRefreshS / 60} minutes`
}

/**
 * Add each source's newest reading to its trend when it is newer than the trend's last point, and drop
 * points older than the window. Returns the same object when nothing changed, so React does not redraw.
 */
export function mergeTrends(
  trends: Record<string, Observation[]>,
  readings: readonly SourceReading[],
  windowFrom: number,
): Record<string, Observation[]> {
  let changed = false
  const out: Record<string, Observation[]> = { ...trends }
  for (const r of readings) {
    const o = r.observation
    const id = r.descriptor.id
    const list = out[id] ?? []
    const last = list[list.length - 1]
    let next = list
    if (o && (last === undefined || o.observedAt > last.observedAt)) next = [...list, o]
    const firstKept = next.findIndex((p) => p.observedAt >= windowFrom)
    if (firstKept > 0) next = next.slice(firstKept)
    else if (firstKept === -1 && next.length > 0) next = []
    if (next !== list) {
      out[id] = next
      changed = true
    }
  }
  return changed ? out : trends
}

/** Apply a changed quick check to what is on screen. */
export function applyQuick(prev: KioskData, readings: SourceReading[], limits: LimitStatus[], windowFrom: number): KioskData {
  return { ...prev, readings, limits, trends: mergeTrends(prev.trends, readings, windowFrom) }
}
